import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function ActionModal({
  open, onClose, requisition, action, subStatusOptions = [], onDone,
  actingAsBanner = null,   // Phase 5.2: super_admin bypass indicator
}) {
  const [remarks, setRemarks] = useState("");
  const [subStatus, setSubStatus] = useState(requisition?.process_owner_sub_status || (subStatusOptions[0] || ""));
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const titleMap = {
    approve: "Approve requisition",
    on_hold: "Place requisition on hold",
    reject: "Reject requisition",
    update_status: "Update procurement status",
  };
  const submitLabel = {
    approve: "Approve",
    on_hold: "Place on hold",
    reject: "Reject",
    update_status: "Save status",
  }[action];

  const btnCls = {
    approve: "btn-vgws btn-teal",
    on_hold: "btn-vgws bg-yellow-600 border-yellow-700 hover:bg-yellow-700",
    reject: "btn-vgws btn-danger",
    update_status: "btn-vgws",
  }[action];

  // Phase 12.1: no min/max length on status remarks anywhere.
  const remarksOk = true;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { action, remarks: remarks.trim() };
      if (action === "update_status") body.sub_status = subStatus;
      const { data } = await api.post(`/requisitions/${requisition.ref_code}/action`, body);
      toast.success(`Action recorded: ${action.replace("_", " ")}`);
      onDone && onDone(data);
      onClose();
      setRemarks("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="action-modal-overlay"
    >
      <div
        className="sheet w-full max-w-lg p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid={`action-modal-${action}`}
      >
        <h3 className="font-serif text-xl font-bold text-[#7a1f2b] mb-1">{titleMap[action] || "Action"}</h3>
        <p className="text-sm text-[#6e7770] mb-4">Requisition <strong>{requisition.ref_code}</strong></p>

        {actingAsBanner && (
          <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 mb-4 flex items-start gap-2"
               data-testid="super-admin-acting-as-banner">
            <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
            <div>
              You are acting as <strong>{actingAsBanner}</strong> on behalf of
              the assigned approver. This action will be logged as a super-admin
              bypass in the audit trail.
            </div>
          </div>
        )}

        {action === "update_status" && (
          <label className="block mb-4">
            <span className="text-sm font-medium text-[#1f2320]">Procurement sub-status</span>
            <select
              className="input-vgws mt-1"
              value={subStatus}
              onChange={(e) => setSubStatus(e.target.value)}
              data-testid="sub-status-select"
            >
              {subStatusOptions.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        )}

        <label className="block">
          <span className="text-sm font-medium text-[#1f2320]">
            Remarks <span className="text-[#b3261e]">*</span>
          </span>
          <textarea
            className="input-vgws mt-1 min-h-[110px]"
            placeholder="Explain your decision in a few lines..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            data-testid="action-remarks-textarea"
          />
        </label>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="action-cancel-button">
            Cancel
          </button>
          <button
            type="button"
            className={btnCls}
            onClick={submit}
            disabled={busy || !remarksOk}
            data-testid={`action-submit-${action}-button`}
          >
            {busy ? "Please wait…" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
